import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';

const API_URL = '';
const BACKEND_URL = 'http://127.0.0.1:8000';
const resolveMediaUrl = (url: string) => {
  if (!url) return url;
  if (url.includes('.mp4') || url.includes('video')) return BACKEND_URL + url;
  return url;
};

interface OptionValue {
  label: string;
  image: string;
  available: boolean;
  price?: number;
  stock?: number;
}

interface ProductOption {
  name: string;
  values: OptionValue[];
}

interface ProductDetail {
  id: number;
  name: string;
  images: string[];
  price: number;
  originalPrice: number;
  discount: number;
  rating: number | null;
  reviews: number | null;
  sold: string | null;
  category: string;
  options: ProductOption[];
  shipping: string;
  videoUrl?: string;
  affiliateLink?: string;
}

interface Props {
  productId: number;
  onClose: () => void;
}

const PreviewModal: React.FC<Props> = ({ productId, onClose }) => {
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(-1); // -1 = 동영상/GIF
  const [optionOverrideImage, setOptionOverrideImage] = useState<string | null>(null); // 옵션 클릭 시 메인 이미지 오버라이드
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: number }>({});
  const [cartMsg, setCartMsg] = useState('');
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/storefront/detail/${productId}`)
      .then(r => r.json())
      .then(data => {
        setProduct(data);
        setSelectedImage(data.videoUrl ? -1 : 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleEsc); document.body.style.overflow = ''; };
  }, [onClose]);

  const formatPrice = (p: number) => p.toLocaleString() + '원';

  if (loading) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
            <div style={{ width: 40, height: 40, border: '3px solid #e62e04', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const images = product.images?.length > 0 ? product.images : ['https://via.placeholder.com/500x500?text=No+Image'];
  const videoUrl = product.videoUrl || '';
  const isVideo = videoUrl.includes('.mp4') || videoUrl.includes('video');
  const isGif = videoUrl.includes('.gif');
  const hasMedia = !!(videoUrl);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* 닫기 버튼 */}
        <button onClick={onClose} style={styles.closeBtn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>

        <div style={styles.content}>
          {/* 좌측: 이미지 영역 */}
          <div style={styles.imageSection}>
            {/* 좌측 썸네일 세로 배열 */}
            <div style={styles.thumbColumn}>
              {/* 동영상/GIF 썸네일 (첫 번째) */}
              {hasMedia && (
                <div
                  style={{
                    ...styles.thumbItem,
                    border: selectedImage === -1 ? '2px solid #222' : '1px solid #e5e5e5',
                    position: 'relative' as const,
                  }}
                  onMouseEnter={() => { setSelectedImage(-1); setOptionOverrideImage(null); }}
                >
                  {isGif ? (
                    <img src={videoUrl} alt="" style={styles.thumbImg} />
                  ) : (
                    <>
                      <img src={images[0]} alt="" style={styles.thumbImg} />
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </>
                  )}
                </div>
              )}
              {images.slice(0, hasMedia ? 5 : 6).map((img, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.thumbItem,
                    border: selectedImage === i ? '2px solid #222' : '1px solid #e5e5e5',
                  }}
                  onMouseEnter={() => { setSelectedImage(i); setOptionOverrideImage(null); }}
                >
                  <img src={img} alt="" style={styles.thumbImg} loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              ))}
            </div>
            {/* 메인 이미지 / 동영상 / GIF */}
            <div style={styles.mainImageWrap}>
              {selectedImage === -2 && optionOverrideImage ? (
                <img
                  src={optionOverrideImage}
                  alt={product.name}
                  style={styles.mainImage}
                  referrerPolicy="no-referrer"
                  onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/500x500?text=No+Image'; }}
                />
              ) : selectedImage === -1 && hasMedia ? (
                isVideo ? (
                  <video
                    src={resolveMediaUrl(videoUrl)}
                    poster={images[0] || ''}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={() => setSelectedImage(0)}
                  />
                ) : (
                  <img src={videoUrl} alt={product.name} style={styles.mainImage}
                    onError={() => setSelectedImage(0)} />
                )
              ) : (
                <img
                  src={images[Math.max(0, selectedImage)]}
                  alt={product.name}
                  style={styles.mainImage}
                  onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/500x500?text=No+Image'; }}
                />
              )}
              {product.discount > 0 && (
                <div style={styles.discountTag}>-{product.discount}%</div>
              )}
            </div>
          </div>

          {/* 우측: 정보 영역 */}
          <div style={styles.infoSection}>
            {/* 상품명 */}
            <h2 style={styles.productName}>{product.name}</h2>

            {/* 평점 + 리뷰 + 판매 */}
            <div style={styles.ratingRow}>
              {!!product.rating && (
                <>
                  <div style={styles.stars}>
                    {[1,2,3,4,5].map(i => (
                      <span key={i} style={{ color: i <= Math.round(product.rating!) ? '#ffa41c' : '#ddd', fontSize: 16 }}>★</span>
                    ))}
                  </div>
                  <span style={styles.ratingText}>{product.rating}</span>
                </>
              )}
              {!!product.reviews && <span style={styles.metaText}>{product.reviews} 리뷰</span>}
              {product.sold && <span style={styles.metaText}>|  {product.sold}</span>}
            </div>

            {/* 가격 영역 */}
            <div style={styles.priceBox}>
              <div style={styles.priceRow}>
                {product.originalPrice > product.price && (
                  <span style={styles.priceOriginal}>원가 {formatPrice(product.originalPrice)}</span>
                )}
              </div>
              <div style={styles.priceMainRow}>
                {product.discount > 0 && !Object.values(selectedOptions).some(i => i >= 0 && product.options?.[0]?.values?.[i]?.price) && (
                  <span style={styles.discountPercent}>{product.discount}%</span>
                )}
                <span style={styles.priceMain}>{formatPrice(
                  (() => {
                    for (const key of Object.keys(selectedOptions)) {
                      const idx = selectedOptions[key];
                      const opt = product.options?.find((o: ProductOption) => o.name === key);
                      const p = opt?.values?.[idx]?.price;
                      if (p && p > 0) return p;
                    }
                    return product.price;
                  })() || 0
                )}</span>
              </div>
            </div>

            {/* 판매자 */}
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>판매자</span>
              <span style={styles.infoValue}>WORLDSHIP(거래 업체)</span>
            </div>

            {/* Choice 배지 + 배송 */}
            <div style={styles.shippingBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={styles.choiceBadge}>Choice</span>
                <span style={{ fontSize: 13, color: '#00b612', fontWeight: 600 }}>무료배송</span>
              </div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                예상 배송일 <b>7~14일</b>
              </div>
            </div>

            {/* 옵션들 */}
            {product.options?.map((opt, optIdx) => (
              opt.values?.length > 0 && (
                <div key={optIdx} style={styles.optionGroup}>
                  <div style={styles.optionLabel}>
                    {opt.name}: <b>{selectedOptions[opt.name] !== undefined ? opt.values[selectedOptions[opt.name]]?.label : ''}</b>
                  </div>
                  <div style={styles.optionValues}>
                    {opt.values.slice(0, 12).map((v, vi) => (
                      <div
                        key={vi}
                        onClick={() => {
                          setSelectedOptions(prev => ({ ...prev, [opt.name]: vi }));
                          if (v.image) {
                            const imgIdx = images.indexOf(v.image);
                            if (imgIdx >= 0) {
                              setSelectedImage(imgIdx);
                              setOptionOverrideImage(null);
                            } else {
                              setOptionOverrideImage(v.image);
                              setSelectedImage(-2);
                            }
                          }
                        }}
                        style={{
                          ...styles.optionBtn,
                          border: selectedOptions[opt.name] === vi ? '2px solid #222' : '1px solid #ddd',
                          background: selectedOptions[opt.name] === vi ? '#f5f5f5' : '#fff',
                          opacity: v.available ? 1 : 0.4,
                        }}
                      >
                        {v.image && (
                          <img src={v.image} alt="" style={styles.optionImg}
                            onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                        )}
                        <span style={{ fontSize: 12 }}>{v.label}</span>
                      </div>
                    ))}
                    {opt.values.length > 12 && (
                      <span style={{ fontSize: 12, color: '#999', padding: '6px 8px' }}>+{opt.values.length - 12}개 더</span>
                    )}
                  </div>
                </div>
              )
            ))}

            {/* 수량 */}
            <div style={styles.quantityRow}>
              <span style={styles.infoLabel}>수량</span>
              <div style={styles.quantityControl}>
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} style={styles.qtyBtn}>−</button>
                <span style={styles.qtyValue}>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} style={styles.qtyBtn}>+</button>
              </div>
              {(() => {
                for (const key of Object.keys(selectedOptions)) {
                  const idx = selectedOptions[key];
                  const opt = product.options?.find((o: ProductOption) => o.name === key);
                  const stock = opt?.values?.[idx]?.stock;
                  if (stock !== undefined && stock > 0) return <span style={{ fontSize: 13, color: stock <= 5 ? '#e62e04' : '#666', marginLeft: 12 }}>재고 {stock}개</span>;
                  if (stock === 0) return <span style={{ fontSize: 13, color: '#e62e04', marginLeft: 12 }}>품절</span>;
                }
                return null;
              })()}
            </div>

            {/* 장바구니 메시지 */}
            {cartMsg && (
              <div style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, textAlign: 'center' as const,
                background: cartMsg.includes('추가') ? '#f0fdf4' : '#fef2f2',
                color: cartMsg.includes('추가') ? '#16a34a' : '#dc2626',
                border: cartMsg.includes('추가') ? '1px solid #bbf7d0' : '1px solid #fecaca' }}>
                {cartMsg}
              </div>
            )}
            {/* 액션 버튼 */}
            <div style={styles.actionRow}>
              <a href="/order" target="_blank" rel="noopener noreferrer" style={styles.cartBtn} onClick={(e) => {
                const optNames = Object.keys(selectedOptions).map(k => {
                  const opt = product.options?.find(o => o.name === k);
                  return opt?.values?.[selectedOptions[k]]?.label;
                }).filter(Boolean).join(' / ');
                const currentPrice = (() => {
                  for (const key of Object.keys(selectedOptions)) {
                    const idx = selectedOptions[key];
                    const opt = product.options?.find(o => o.name === key);
                    const p = opt?.values?.[idx]?.price;
                    if (p && p > 0) return p;
                  }
                  return product.price;
                })();
                const orderData = {
                  items: [{
                    product_id: product.id,
                    product_name: product.name,
                    main_image: product.images?.[0] || '',
                    price: currentPrice,
                    quantity,
                    option_name: optNames || undefined
                  }]
                };
                localStorage.setItem('pendingOrder', JSON.stringify(orderData));
                setTimeout(() => onClose(), 100);
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" style={{ marginRight: 6 }}>
                  <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0020.01 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                </svg>
                구매하기
              </a>
              <a href={`/product/${product.id}`} target="_blank" rel="noopener noreferrer" onClick={onClose} style={styles.detailBtn}>
                상세 보기
              </a>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.6)', zIndex: 9999,
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    backdropFilter: 'blur(2px)',
  },
  modal: {
    background: '#fff', borderRadius: 12, position: 'relative',
    width: '90%', maxWidth: 1500, height: 700, overflow: 'hidden',
    boxShadow: '0 25px 80px rgba(0,0,0,0.35)',
  },
  closeBtn: {
    position: 'absolute', top: 16, right: 16, zIndex: 10,
    width: 36, height: 36, borderRadius: '50%', border: 'none',
    background: '#f5f5f5', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.2s',
  },
  content: {
    display: 'flex', padding: '20px 24px', gap: 24, height: '100%',
  },
  // 이미지 영역
  imageSection: {
    flex: '0 0 460px', display: 'flex', gap: 10, height: '100%',
  },
  thumbColumn: {
    display: 'flex', flexDirection: 'column' as const, gap: 6, width: 64,
  },
  thumbItem: {
    width: 60, height: 60, borderRadius: 6, overflow: 'hidden',
    cursor: 'pointer', transition: 'border 0.15s', flexShrink: 0,
  },
  thumbImg: {
    width: '100%', height: '100%', objectFit: 'cover' as const,
  },
  mainImageWrap: {
    flex: 1, aspectRatio: '1', borderRadius: 10, overflow: 'hidden',
    background: '#fafafa', position: 'relative' as const,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  mainImage: {
    width: '100%', height: '100%', objectFit: 'contain' as const,
  },
  discountTag: {
    position: 'absolute' as const, top: 12, left: 12,
    background: '#e62e04', color: '#fff', padding: '4px 10px',
    borderRadius: 4, fontSize: 14, fontWeight: 700,
  },
  // 정보 영역
  infoSection: {
    flex: 1, display: 'flex', flexDirection: 'column' as const, gap: 10,
    minWidth: 0, overflowY: 'auto' as const, height: '100%', paddingRight: 8,
  },
  productName: {
    fontSize: 18, fontWeight: 600, color: '#222', lineHeight: 1.5,
    margin: 0, paddingRight: 32,
  },
  ratingRow: {
    display: 'flex', alignItems: 'center', gap: 6,
  },
  stars: { display: 'flex', gap: 1 },
  ratingText: { fontSize: 14, color: '#666', fontWeight: 500 },
  metaText: { fontSize: 13, color: '#999' },
  // 가격
  priceBox: {
    background: '#fff4f0', padding: '14px 18px', borderRadius: 10,
  },
  priceRow: { marginBottom: 4 },
  priceOriginal: { fontSize: 13, color: '#999', textDecoration: 'line-through' },
  priceMainRow: { display: 'flex', alignItems: 'baseline', gap: 10 },
  discountPercent: { color: '#e62e04', fontSize: 28, fontWeight: 800 },
  priceMain: { color: '#222', fontSize: 28, fontWeight: 800 },
  // 정보 행
  infoRow: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '8px 0', borderBottom: '1px solid #f0f0f0',
  },
  infoLabel: { fontSize: 13, color: '#999', minWidth: 48 },
  infoValue: { fontSize: 14, color: '#333', fontWeight: 500 },
  // 배송
  shippingBox: {
    padding: '10px 0',
  },
  choiceBadge: {
    background: '#222', color: '#fff', padding: '2px 8px',
    borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
  },
  // 옵션
  optionGroup: { padding: '6px 0' },
  optionLabel: { fontSize: 13, color: '#666', marginBottom: 8 },
  optionValues: { display: 'flex', flexWrap: 'wrap' as const, gap: 6 },
  optionBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', borderRadius: 24, cursor: 'pointer',
    transition: 'all 0.15s', fontSize: 13,
  },
  optionImg: { width: 28, height: 28, borderRadius: 5, objectFit: 'cover' as const },
  // 수량
  quantityRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0',
  },
  quantityControl: { display: 'flex', alignItems: 'center' },
  qtyBtn: {
    width: 34, height: 34, border: '1px solid #ddd', background: '#fff',
    cursor: 'pointer', fontSize: 18, borderRadius: 4, color: '#333',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  qtyValue: {
    width: 50, textAlign: 'center' as const, fontSize: 15, fontWeight: 600,
    borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd',
    height: 34, lineHeight: '34px',
  },
  // 액션
  actionRow: {
    display: 'flex', gap: 12, marginTop: 'auto', paddingTop: 14,
  },
  cartBtn: {
    flex: 1, padding: '14px 0', background: '#e62e04', color: '#fff',
    border: 'none', borderRadius: 26, fontSize: 16, fontWeight: 700,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.2s', textDecoration: 'none',
  },
  detailBtn: {
    flex: 1, padding: '14px 0', background: '#fff', color: '#222',
    border: '2px solid #222', borderRadius: 26, fontSize: 16, fontWeight: 700,
    cursor: 'pointer', textDecoration: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s',
  },
};

export default PreviewModal;
